import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Database, Server, Zap, Loader2 } from "lucide-react";

const PendingApproval = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [dots, setDots] = useState('');
  
  const organizationName = location.state?.organizationName || 'Your Organization';
  const organizationSlug = location.state?.organizationSlug || 'N/A';

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative" style={{background: '#f8f9fa'}}>
      {/* CSS Confetti Animation */}
      <style>{`
        @keyframes confetti-fall {
          0% { transform: translateY(-100vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .confetti {
          position: fixed;
          width: 10px;
          height: 10px;
          background: #007AFF;
          animation: confetti-fall 3s linear forwards;
        }
      `}</style>
      
      {/* Generate confetti pieces with CSS */}
      {[...Array(50)].map((_, i) => (
        <div
          key={i}
          className="confetti"
          style={{
            left: `${Math.random() * 100}%`,
            top: '-20px',
            background: ['#007AFF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'][i % 5],
            animationDelay: `${Math.random() * 2}s`,
            animationDuration: `${2 + Math.random() * 2}s`
          }}
        />
      ))}
      
      <Card className="w-full max-w-lg shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
        <CardHeader className="text-center pt-16 pb-6">
          {/* Animated Database Icons */}
          <div className="flex justify-center gap-4 mb-6">
            <div className="p-3 rounded-xl shadow-lg bg-blue-100 animate-bounce" style={{ animationDelay: '0ms' }}>
              <Database className="w-8 h-8 text-blue-600" />
            </div>
            <div className="p-3 rounded-xl shadow-lg bg-green-100 animate-bounce" style={{ animationDelay: '150ms' }}>
              <Server className="w-8 h-8 text-green-600" />
            </div>
            <div className="p-3 rounded-xl shadow-lg bg-purple-100 animate-bounce" style={{ animationDelay: '300ms' }}>
              <img 
                src="/logo-icon.png" 
                alt="BookingFlows" 
                className="w-8 h-8 rounded-lg object-cover"
              />
            </div>
          </div>
          
          <CardTitle className="text-3xl font-bold mb-4" style={{color: '#1a1a1a'}}>
            🎉 Welcome to BookingFlows!
          </CardTitle>
          <p className="text-gray-600 text-lg">
            The team will reach out when setup is complete{dots}
          </p>
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {/* Loading Animation - Clean card */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 mb-6">
            <div className="space-y-2 text-xs text-gray-600">
              <p>✓ Creating your database</p>
              <p>✓ Setting up your workspace</p>
              <p>✓ Configuring your environment</p>
            </div>
          </div>

          {/* Message */}
          <div className="text-center mb-6">
            <p className="text-gray-700 mb-3">
              🎊 <strong>We're thrilled to have you!</strong>
            </p>
            <p className="text-sm text-gray-600">
              Our team is working on launching your database. We'll reach out soon to get you started!
            </p>
          </div>

          {/* Org Info */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <p className="text-xs text-gray-500 mb-1">Your Organization</p>
            <p className="text-lg font-bold text-gray-900">{organizationName}</p>
            {organizationSlug !== 'N/A' && (
              <p className="text-sm text-gray-600 mt-1">
                Shortname: <span className="font-mono font-semibold">{organizationSlug}</span>
              </p>
            )}
          </div>

          <Button
            onClick={() => navigate('/')}
            className="w-full h-12 text-base font-semibold"
            style={{background: '#007AFF'}}
          >
            Back to Home
          </Button>
        </CardContent>
        
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            © 2025 BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default PendingApproval;
